/**
 * DepartmentHeroCover — hero banner with optional cover image + member profile photo.
 * Cover: Firestore departments/{portalId}.coverImageUrl
 * Profile: Firestore authorized_users/{userId}.photoURL
 */
import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth, updateProfile } from 'firebase/auth';
import { resolveUserPhotoURL } from '../UserAvatar/UserAvatar';
import { isV2 } from '../../design/designFlags';
import '../../design/v2.css';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

function initials(name = '') {
  const p = String(name).trim().split(/\s+/).filter(Boolean);
  if (p.length > 1) return (p[0][0] + p[p.length - 1][0]).toUpperCase();
  return (p[0]?.[0] || '?').toUpperCase();
}

function HeroProfileAvatar({ db, userId, profileUserId, userName, portal, theme, readOnly, allowProfileEdit = true, coverUrl }) {
  const [photoURL, setPhotoURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [dpError, setDpError] = useState('');
  const dpRef = useRef(null);

  const accent = portal?.color || theme?.accent || '#6366f1';
  const photoUid = profileUserId !== undefined ? profileUserId : userId;
  const canEdit = allowProfileEdit && !readOnly && !!photoUid;

  useEffect(() => {
    if (!db || !photoUid) {
      setPhotoURL(photoUid ? resolveUserPhotoURL({}) || null : null);
      return undefined;
    }
    const unsub = onSnapshot(doc(db, 'authorized_users', photoUid), snap => {
      const stored = snap.exists() ? snap.data().photoURL : null;
      setPhotoURL(stored || (photoUid === userId ? resolveUserPhotoURL({}) : null) || null);
      setImgFailed(false);
    }, () => {
      setPhotoURL(photoUid === userId ? resolveUserPhotoURL({}) || null : null);
    });
    return unsub;
  }, [db, photoUid, userId]);

  async function handleDpUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canEdit || !db) return;
    if (!file.type.startsWith('image/')) {
      setDpError('Please choose a JPG, PNG, WebP, or GIF.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setDpError('Photo must be under 4 MB.');
      return;
    }

    setDpError('');
    setUploading(true);
    setUploadPct(0);
    try {
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `profile_photos/${photoUid}/${Date.now()}_${safeName}`;
      const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });
      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve,
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      await setDoc(doc(db, 'authorized_users', photoUid), {
        photoURL: url,
        photoUpdatedAt: serverTimestamp(),
      }, { merge: true });
      try {
        const authUser = getAuth().currentUser;
        if (authUser?.uid === photoUid) await updateProfile(authUser, { photoURL: url });
      } catch (_) {}
      setPhotoURL(url);
      setImgFailed(false);
    } catch (ex) {
      setDpError(ex.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  }

  const showPhoto = photoURL && !imgFailed;
  const label = uploading ? `${uploadPct}%` : canEdit ? 'Change photo' : userName;

  return (
    <div className="smc-hero-avatar-wrap">
      <input
        ref={dpRef}
        type="file"
        accept={ACCEPT}
        onChange={handleDpUpload}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="smc-hero-avatar"
        style={{ '--avatar-accent': accent }}
        disabled={!canEdit || uploading}
        onClick={() => canEdit && dpRef.current?.click()}
        title={canEdit ? 'Change profile photo' : userName}
        aria-label={canEdit ? 'Change profile photo' : `${userName} profile photo`}
      >
        {showPhoto ? (
          <img
            src={photoURL}
            alt={userName ? `${userName} profile` : 'Profile'}
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span className="smc-hero-avatar-initials">{initials(userName)}</span>
        )}
        {canEdit && (
          <span className="smc-hero-avatar-overlay" aria-hidden="true">
            {uploading ? `${uploadPct}%` : '📷'}
          </span>
        )}
      </button>
      {canEdit && (
        <span className="smc-hero-avatar-hint" style={{ color: coverUrl ? 'rgba(255,255,255,0.75)' : theme?.muted }}>
          {label}
        </span>
      )}
      {dpError && (
        <span className="smc-hero-avatar-error">{dpError}</span>
      )}
    </div>
  );
}

// Company banner shown when a department has no cover of its own. Display
// only — nothing is written, so covers already set are never replaced.
const DEFAULT_COVER = '/default-cover.jpg';

export default function DepartmentHeroCover({
  db,
  portal,
  theme,
  userId,
  profileUserId,
  userName,
  role,
  readOnly = false,
  allowProfileEdit = true,
}) {
  const [coverUrl, setCoverUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const portalId = portal?.id;
  const canEdit = !readOnly && !!userId && !!portalId;

  useEffect(() => {
    if (!db || !portalId) {
      setCoverUrl(null);
      return undefined;
    }
    const unsub = onSnapshot(doc(db, 'departments', portalId), snap => {
      setCoverUrl(snap.exists() ? (snap.data().coverImageUrl || null) : null);
    }, () => setCoverUrl(null));
    return unsub;
  }, [db, portalId]);

  async function persistCover(url) {
    await setDoc(doc(db, 'departments', portalId), {
      coverImageUrl: url || null,
      coverImageUpdatedAt: serverTimestamp(),
      coverImageUpdatedBy: userName || userId,
      coverImageUpdatedByUid: userId,
    }, { merge: true });
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canEdit) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose a JPG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Image must be under 8 MB.');
      return;
    }

    setError('');
    setUploading(true);
    setUploadPct(0);
    try {
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `department_covers/${portalId}/${Date.now()}_${safeName}`;
      const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });
      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve,
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      await persistCover(url);
    } catch (ex) {
      setError(ex.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  }

  async function removeCover() {
    if (!canEdit || !coverUrl) return;
    if (!window.confirm('Remove this cover and go back to the default Spacemancraft banner?')) return;
    setError('');
    try {
      await persistCover(null);
    } catch (ex) {
      setError(ex.message || 'Could not remove cover.');
    }
  }

  const accent = portal?.color || theme?.accent || '#6366f1';
  const shownCover = coverUrl || DEFAULT_COVER;
  // v2: the cover stays a clean banner and the title sits in a card at its
  // bottom edge, so text never overlaps the artwork.
  const cardLayout = isV2('shell');
  const coverLayer = cardLayout
    ? 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%)'
    : coverUrl
    ? 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 100%)'
    // default banner: darker centre keeps the title readable over its logo
    : 'radial-gradient(ellipse at center, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0.25) 100%)';

  return (
    <div
      className={`smc-hero smc-hero-cover${cardLayout ? ' smc2-herocover' : ''}`}
      style={{
        '--hero-accent': accent,
        '--hero-bg': theme?.bg || '#0f172a',
        backgroundColor: '#000',
        backgroundImage: `${coverLayer}, url(${shownCover})`,
      }}
    >
      {canEdit && (
        <div className="smc-hero-cover-actions">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="smc-hero-cover-btn"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? `UPLOADING ${uploadPct}%` : '✎ CHANGE COVER'}
          </button>
          {coverUrl && !uploading && (
            <button type="button" className="smc-hero-cover-btn smc-hero-cover-btn-muted" onClick={removeCover}>
              ↺ USE DEFAULT
            </button>
          )}
        </div>
      )}

      {cardLayout ? (
        <div className="smc-hero-cover-content smc2-herocard" style={{ '--dept': accent }}>
          <HeroProfileAvatar
            db={db}
            userId={userId}
            profileUserId={profileUserId}
            userName={userName}
            portal={portal}
            theme={theme}
            readOnly={readOnly}
            allowProfileEdit={allowProfileEdit}
            coverUrl={shownCover}
          />
          <div style={{ minWidth: 0 }}>
            <div className="smc2-hud" style={{ color: accent }}>SPACEMANCRAFT — {portal.label.toUpperCase()}</div>
            <h1 className="smc2-herocard-title">{portal.label}</h1>
            {portal.desc && <p className="smc2-herocard-desc">{portal.desc}</p>}
          </div>
        </div>
      ) : (
      <div className="smc-hero-cover-content">
        <div className="smc-hero-eyebrow" style={{ color: `${accent}cc` }}>
          SPACEMANCRAFT — {portal.label.toUpperCase()}
        </div>
        <HeroProfileAvatar
          db={db}
          userId={userId}
          profileUserId={profileUserId}
          userName={userName}
          portal={portal}
          theme={theme}
          readOnly={readOnly}
          allowProfileEdit={allowProfileEdit}
          coverUrl={shownCover}
        />
        <h1
          className="smc-hero-title"
          style={{
            color: '#fff',
          }}
        >
          {portal.label}
        </h1>
        <p className="smc-hero-desc" style={{ color: 'rgba(255,255,255,0.82)' }}>
          {portal.desc}
        </p>
      </div>
      )}

      {error && (
        <div className="smc-hero-cover-error">{error}</div>
      )}
    </div>
  );
}
